// src/features/analytics/components/MediaRetentionPanel.tsx
//
// Audience retention — the per-media drop-off curve inside the Content-tab
// Drawer (metrics expansion; VOD only, the Drawer gates on streamType). The
// wire buckets are server-computed suffix data; retention.ts derives the
// headline stats and chart rows. Honesty is load-bearing: the server's
// <10-session guard (hasData=false) renders a low-sample notice and NEVER a
// curve, and an empty envelope renders the standard coverage notice.

import { Alert, Paper, SimpleGrid, Skeleton, Text, Title } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { IconInfoCircle } from '@tabler/icons-react';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { retentionChartRows, retentionStats, type RetentionChartRow } from '../retention';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import { StatTile } from './StatTile';
import type { AnalyticsEnvelope, MediaRetentionData } from '../types';

interface MediaRetentionPanelProps {
  retention: AnalyticsEnvelope<MediaRetentionData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
}

const LOW_SAMPLE_COPY =
  'Not enough viewers yet — retention appears once a video has 10+ sessions.';

/** Quartile guides — recessive, matching the grid rather than the series. */
const QUARTILE_LINES = [25, 50, 75].map((y) => ({ y, color: 'gray.4' }));

/** 'At 12:40 (60%) — 41% still watching (n=210)'; percent-only when duration is unknown. */
function RetentionTooltip({ payload }: { payload?: unknown }) {
  const rows = payload as { payload?: RetentionChartRow }[] | undefined;
  const row = rows?.[0]?.payload;
  if (!row) return null;
  const position = row.timeLabel !== null ? `${row.timeLabel} (${row.pctLabel})` : row.pctLabel;
  return (
    <Paper withBorder shadow="sm" p="xs">
      <Text size="xs">
        At {position} — {Math.round(row.pctViewers)}% still watching (n=
        {AnalyticsUtils.formatCount(row.viewersReached)})
      </Text>
    </Paper>
  );
}

export function MediaRetentionPanel({
  retention,
  isLoading,
  isError,
  error,
  onRetry,
}: MediaRetentionPanelProps) {
  const data = retention?.data ?? null;
  const stats = data ? retentionStats(data) : null;
  const chartRows = data ? retentionChartRows(data) : [];
  const isEnvelopeEmpty =
    retention !== null && (!retention.coverage || retention.freshness?.state === 'empty');

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        Audience retention
      </Title>
      {isLoading ? (
        <Skeleton height={300} />
      ) : isError ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : isEnvelopeEmpty ? (
        <AnalyticsCoverageNotice
          coverage={retention?.coverage}
          freshness={retention?.freshness}
          emptyMessage="No retention data for this media in the selected range."
        />
      ) : data && !data.hasData ? (
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>
          {LOW_SAMPLE_COPY}
        </Alert>
      ) : data && stats && chartRows.length > 0 ? (
        <>
          <AnalyticsCoverageNotice
            coverage={retention?.coverage}
            freshness={retention?.freshness}
          />
          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm" mb="md">
            <StatTile
              label="Completion rate"
              value={AnalyticsUtils.formatPercent(stats.completionRate)}
            />
            <StatTile
              label="Median watched"
              value={AnalyticsUtils.formatPercent(stats.medianWatchedPct / 100)}
            />
            <StatTile label="Sessions" value={AnalyticsUtils.formatCount(data.totalSessions)} />
          </SimpleGrid>
          <AreaChart
            h={220}
            data={chartRows}
            dataKey="label"
            series={[
              { name: 'pctViewers', label: '% of viewers still watching', color: 'blue.6' },
            ]}
            withGradient
            curveType="monotone"
            withDots={false}
            yAxisLabel="% of viewers still watching"
            yAxisProps={{ domain: [0, 100], ticks: [0, 25, 50, 75, 100] }}
            xAxisProps={{ interval: 1 }}
            referenceLines={QUARTILE_LINES}
            tooltipProps={{
              content: ({ payload }) => <RetentionTooltip payload={payload} />,
            }}
          />
          <Text size="xs" c="dimmed" mt={4}>
            Share of sessions still watching at each point of the video
            {data.contentDurationMs === 0 && ' (duration unknown — positions shown as percents)'}
            .
          </Text>
        </>
      ) : (
        // hasData=true but unchartable (defensive: zero sessions / missing buckets).
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>
          {LOW_SAMPLE_COPY}
        </Alert>
      )}
    </Paper>
  );
}
